import type { PlacementPosition, Surface, SurfacePoint } from '@repo/api-contracts';
import type { RefObject } from 'react';
import {
  cylinderMmToPhoto,
  type PhotoDimensions,
  type ShapeRingKind,
  shapeRingLimitOf,
  surfaceMmToPhoto,
  surfacePhotoToMm,
  validateShapeRing,
} from '@/entities/edit-area';
import { usePolygonPointer } from './use-polygon-pointer';

interface Options {
  svgRef: RefObject<SVGSVGElement | null>;
  photoSize: PhotoDimensions;
  /** 점을 옮길 표면. 없거나 mesh 면 점을 찍지 않는다. */
  surface: Surface | undefined;
  /** 그리기 모드. 꺼지면 열린 경로를 버린다. */
  active: boolean;
  /** 닫은 표면 mm 점열. 거부 사유를 돌려주면 점을 남기고 사유를 보인다. */
  onClose: (points: PlacementPosition[]) => string | null;
  /** Esc 로 그리기를 그만둘 때. */
  onCancel: () => void;
  /** 그리는 고리 — 인쇄 영역(점 최대 256개) 또는 인쇄 제외 영역(점 최대 64개). */
  kind?: ShapeRingKind;
}

/** 표면 mm → 사진 px. 평면은 선형, 원통은 원통 정변환이다. mesh 나 변환 실패는 null. */
const mmToPhoto = (
  surface: Surface | undefined,
  photoSize: PhotoDimensions,
  point: PlacementPosition,
): SurfacePoint | null => {
  if (surface?.type === 'flat') return surfaceMmToPhoto(surface, photoSize, point);
  if (surface?.type === 'cylinder') return cylinderMmToPhoto(surface, photoSize, point);
  return null;
};

/**
 * 인쇄 영역 그리기 — 운영자가 사진 위에 점을 찍어 자유 다각형을 닫는다. 점은 표면 mm 로 모으고(원통은 실루엣 안만),
 * 첫 점 판정과 오버레이는 사진 px 로 되돌려 한다.
 */
export const useShapePointer = ({
  svgRef,
  photoSize,
  surface,
  active,
  onClose,
  onCancel,
  kind = 'area',
}: Options) => {
  const limit = shapeRingLimitOf(kind);
  const toPhoto = (point: PlacementPosition): SurfacePoint | null =>
    mmToPhoto(surface, photoSize, point);
  const pointer = usePolygonPointer<PlacementPosition>({
    svgRef,
    photoSize,
    active,
    toPoint: (photoPx) =>
      surface === undefined ? null : surfacePhotoToMm(surface, photoSize, photoPx),
    distancePx: (a, b) => {
      const pa = toPhoto(a);
      const pb = toPhoto(b);
      return pa === null || pb === null
        ? Number.POSITIVE_INFINITY
        : Math.hypot(pa.x - pb.x, pa.y - pb.y);
    },
    validate: (points) => validateShapeRing(points, kind),
    maxPoints: limit.maxPoints,
    maxPointsMessage: `${limit.message} 첫 점을 눌러 닫아 주세요.`,
    outsideMessage: '상품 실루엣 안을 눌러 주세요.',
    onClose,
    onCancel,
  });
  const pointsPx = pointer.points.flatMap((point) => {
    const px = toPhoto(point);
    return px === null ? [] : [px];
  });
  return { ...pointer, pointsPx };
};
